import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Calendar, Edit, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MemberProfile({ user }) {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMember();
  }, [memberId]);

  const fetchMember = async () => {
    try {
      const response = await axios.get(`${API}/members`, {
        withCredentials: true
      });
      const foundMember = response.data.find(m => m.member_id === memberId);
      setMember(foundMember);
    } catch (error) {
      console.error('Error fetching member:', error);
      toast.error('Failed to load member profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif text-foreground mb-4">Member Not Found</h2>
          <Button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
          className="mb-6 text-foreground-muted hover:text-primary"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Back to Tree
        </Button>

        <div className="bg-white border border-border shadow-card rounded-lg overflow-hidden">
          {/* Header */}
          <div className="p-8 bg-paper border-b border-border">
            <div className="flex items-start space-x-6">
              {member.profile_pic ? (
                <img
                  src={member.profile_pic}
                  alt={member.name}
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-accent flex items-center justify-center">
                  <UserIcon className="w-12 h-12 text-primary" strokeWidth={1.5} />
                </div>
              )}

              <div className="flex-1">
                <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2" data-testid="member-name">
                  {member.name}
                </h1>
                
                <div className="flex flex-wrap gap-4 text-sm font-sans text-foreground-muted">
                  {member.location && (
                    <div className="flex items-center" data-testid="member-location">
                      <MapPin className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      {member.location}
                    </div>
                  )}
                  {member.birth_date && (
                    <div className="flex items-center" data-testid="member-birth-date">
                      <Calendar className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      Born: {member.birth_date}
                    </div>
                  )}
                  {member.death_date && (
                    <div className="flex items-center" data-testid="member-death-date">
                      <Calendar className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      Died: {member.death_date}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bio */}
          {member.bio && (
            <div className="p-8 border-b border-border">
              <h2 className="text-2xl font-serif text-foreground mb-4">Biography</h2>
              <p className="text-base font-sans text-foreground-muted leading-relaxed" data-testid="member-bio">
                {member.bio}
              </p>
            </div>
          )}

          {/* Relationships */}
          <div className="p-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">Family Connections</h2>
            
            <div className="space-y-4">
              {member.parent_ids && member.parent_ids.length > 0 && (
                <div>
                  <h3 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-2">Parents</h3>
                  <p className="text-base font-sans text-foreground">{member.parent_ids.length} parent(s)</p>
                </div>
              )}
              
              {member.spouse_ids && member.spouse_ids.length > 0 && (
                <div>
                  <h3 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-2">Spouse(s)</h3>
                  <p className="text-base font-sans text-foreground">{member.spouse_ids.length} spouse(s)</p>
                </div>
              )}
              
              {member.children_ids && member.children_ids.length > 0 && (
                <div>
                  <h3 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-2">Children</h3>
                  <p className="text-base font-sans text-foreground">{member.children_ids.length} child(ren)</p>
                </div>
              )}

              {(!member.parent_ids || member.parent_ids.length === 0) && 
               (!member.spouse_ids || member.spouse_ids.length === 0) && 
               (!member.children_ids || member.children_ids.length === 0) && (
                <p className="text-base font-sans text-foreground-muted italic">No family connections recorded</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}